import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { HERO } from "@/constants/testIds";
import RevealLines from "@/components/RevealLines";
import { scrollToId } from "@/hooks/useSmoothScroll";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);

  return (
    <section
      id="home"
      ref={ref}
      data-testid={HERO.section}
      className="relative min-h-[100svh] pt-32 md:pt-36 pb-16 md:pb-24 overflow-hidden"
    >
      {/* Ambient shapes */}
      <div
        aria-hidden
        className="absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full blur-3xl opacity-30"
        style={{ background: "radial-gradient(closest-side, hsl(var(--accent)), transparent 70%)" }}
      />
      <div
        aria-hidden
        className="absolute bottom-0 right-[-160px] w-[420px] h-[420px] rounded-full blur-3xl opacity-25"
        style={{ background: "radial-gradient(closest-side, hsl(var(--secondary)), transparent 70%)" }}
      />

      <div className="relative mx-auto max-w-7xl px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* Left: Kinetic headline */}
          <div className="lg:col-span-7 order-2 lg:order-1">
            <RevealLines
              as="h1"
              className="font-serif font-light text-[13vw] leading-[0.95] sm:text-[10vw] md:text-[8vw] lg:text-[7.2vw] xl:text-[7rem] tracking-tight text-foreground"
              lines={["Neha", "Gupta ,", <em key="e1" className="italic text-primary font-normal">psychodynamic</em>, <em key="e2" className="italic text-primary font-normal">psychotherapist.</em>]}
              lineClassName=""
              stagger={0.12}
              duration={1.1}
            />

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.3, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="mt-6 flex items-center gap-3"
            >
              <span className="h-px w-8 bg-foreground/40" />
              <span className="text-xs md:text-sm uppercase tracking-[0.3em] text-muted-foreground">
                Since 2013
              </span>
            </motion.div>            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.4, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
              className="mt-10 max-w-xl text-base md:text-lg leading-relaxed text-muted-foreground"
            >
              A safe, confidential, and empathetic space to explore, heal,
              and grow — through an honest and relational psychodynamic lens.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.6, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <button
                data-testid={HERO.primaryCta}
                onClick={() => scrollToId("contact")}
                className="group relative inline-flex items-center gap-3 pl-6 pr-2 py-2 rounded-full bg-foreground text-background hover:bg-primary transition-colors duration-500"
              >
                <span className="text-sm md:text-base tracking-wide">Request an Appointment</span>
                <span className="w-10 h-10 rounded-full bg-background/15 flex items-center justify-center group-hover:rotate-45 transition-transform duration-500">
                  <ArrowUpRight size={18} />
                </span>
              </button>
              <button
                data-testid={HERO.secondaryCta}
                onClick={() => scrollToId("about")}
                className="text-sm text-foreground/80 underline-grow px-2 py-2"
              >
                Read her story
              </button>
            </motion.div>
          </div>

          {/* Right: Portrait */}
          <div className="lg:col-span-5 order-1 lg:order-2 relative">
            <motion.div
              style={{ y, scale }}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1], delay: 0.3 }}
              className="relative mx-auto w-full max-w-[420px]"
            >
              {/* Frame ring */}
              <div className="absolute -inset-4 rounded-[3rem] bg-accent/40 blur-2xl" aria-hidden />
              <div className="relative aspect-[5/6] w-full overflow-hidden rounded-[3rem] shadow-[0_30px_80px_-30px_rgba(54,46,41,0.35)]">
                <img
                  src="/neha.jpg"
                  alt="Portrait of Neha Gupta"
                  data-testid={HERO.portrait}
                  className="w-full h-full object-cover"
                  loading="eager"
                />
                {/* Warm spotlight overlay */}
                <div
                  aria-hidden
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background:
                      "radial-gradient(120% 80% at 30% 20%, hsl(var(--background)/0.0) 40%, hsl(var(--primary)/0.15) 100%)",
                  }}
                />
              </div>
              {/* Caption tag */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.6, duration: 0.8 }}
                className="absolute -bottom-6 -left-4 md:-left-8 bg-card px-5 py-3 rounded-full border border-border shadow-sm"
              >
                <span className="text-xs uppercase tracking-[0.25em] text-muted-foreground">
                  Panjim, Goa · Independent Practice
                </span>
              </motion.div>
            </motion.div>
          </div>
        </div>

        {/* Bottom meta strip */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.9, duration: 1 }}
          className="mt-24 md:mt-28 flex flex-col md:flex-row items-start md:items-end justify-between gap-6 border-t border-border pt-8"
        >
          <p className="font-serif italic text-2xl md:text-3xl text-foreground/80 max-w-md leading-snug">
            &ldquo;If it matters to you, it is worth talking about.&rdquo;
          </p>
          <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground">
            Adults · Adolescents · Young Adults
          </div>
        </motion.div>
      </div>
    </section>
  );
}
